// Team Cup scorekeeper.
//
// Activated when ScoreApp receives a tournament with info.format === 'Team Cup'.
// The `team` query param holds the GROUP CODE (e.g. ?t=tid&team=ABCD); we
// resolve it to a group inside /teamCup/groups.

import React, { useEffect, useMemo, useState } from 'react'
import {
  watchTeamCup, setTeamCupScore, setTeamCupCth,
} from '../lib/teamCupData.js'
import {
  computeTeamMatchResults, computeScatPayouts, computeCthValues,
  strokesForRound, DEFAULT_STROKE_INDEX, allocateStrokes,
} from '../lib/teamCupScoring.js'
import { calcCourseHandicap, calcPlayingHandicap } from '../lib/handicap.js'

export default function TeamCupScore({ tournament, tournamentId, groupKey, pars }) {
  const info = tournament?.info || {}
  const config = info.teamCupConfig || {}
  const players = entriesAsList(tournament?.players)
  const [data, setData] = useState({})
  const [hole, setHole] = useState(1)

  useEffect(() => {
    const unsub = watchTeamCup(tournamentId, setData)
    return () => unsub()
  }, [tournamentId])

  const groups = entriesAsList(data?.groups)
  const teams = entriesAsList(data?.teams)
  // Match by code OR id so admins can paste either form.
  const group = groups.find(g => g.code === groupKey?.toUpperCase()) || groups.find(g => g.id === groupKey)
  const scoresByGroup = data?.scores || {}
  const cthSubs = data?.cth || {}
  const myScores = group ? (scoresByGroup[group.id] || {}) : {}

  // Stroke allocation when handicaps on.
  const handicapsOn = !!config.handicapsOn && (info.handicapMode && info.handicapMode !== 'none')
  const slopeRating = Number(info?.slopeRating) || 113
  const courseRating = Number(info?.courseRating) || (Number(info?.coursePar) || 72)
  const coursePar = Number(info?.coursePar) || 72
  const allowance = Number(info?.handicapAllowance) || 100
  const courseHandicaps = useMemo(() => {
    const out = {}
    if (!handicapsOn) return out
    for (const p of players) {
      const hi = Number(p.handicapIndex)
      if (!Number.isFinite(hi)) continue
      const ch = calcCourseHandicap(hi, slopeRating, courseRating, coursePar)
      const ph = calcPlayingHandicap(ch, allowance)
      out[p.id] = ph
    }
    return out
  }, [handicapsOn, players, slopeRating, courseRating, coursePar, allowance])
  const strokesByPlayer = useMemo(
    () => strokesForRound(courseHandicaps, DEFAULT_STROKE_INDEX),
    [courseHandicaps]
  )

  // Subset of teams active in this group.
  const groupTeams = useMemo(() => {
    if (!group) return []
    const gids = new Set(group.playerIds || [])
    return teams
      .map(t => ({
        id: t.id,
        name: t.name || 'Team',
        playerIds: (t.playerIds || []).filter(pid => gids.has(pid)),
      }))
      .filter(t => t.playerIds.length > 0)
  }, [group, teams])

  const matchResult = useMemo(() => computeTeamMatchResults({
    teams: groupTeams,
    scoresByHole: myScores,
    strokesByPlayer,
    handicapsOn,
    scoringSystem: config.scoringSystem || 'official',
  }), [groupTeams, myScores, strokesByPlayer, handicapsOn, config.scoringSystem])

  // Tournament-wide Scats (live counts).
  const allPlayerIds = useMemo(
    () => Array.from(new Set(groups.flatMap(g => g.playerIds || []))),
    [groups]
  )
  const scats = useMemo(() => config.scatsEnabled
    ? computeScatPayouts({
        playerIds: allPlayerIds, scoresByGroup,
        perHole: Number(config.scatsPerHole) || 0, roundFinal: false,
      })
    : null, [config.scatsEnabled, allPlayerIds, scoresByGroup, config.scatsPerHole])

  // Header info.
  const par = pars?.[hole - 1] ?? 4
  const si = DEFAULT_STROKE_INDEX[hole - 1]
  const isPar3 = par === 3
  const cthSubmittedHere = group ? cthSubs?.[hole]?.[group.id]?.v : null

  if (!group) {
    return (
      <Shell tournament={tournament}>
        <div className="bg-white rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold text-red-700">Group code not found</h2>
          <p className="text-sm text-gray-600 mt-2">
            {groupKey
              ? <>No Team Cup group matches code <strong>{groupKey}</strong>.</>
              : 'Pass the group code as ?team=… in the URL.'}
          </p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell tournament={tournament} subtitle={`Group ${group.code} · ${group.name || ''}`}>
      <div className="space-y-3">

        {/* Team running totals */}
        <div className="bg-white rounded-lg p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2">Team Totals · {config.scoringSystem === 'custom' ? 'Custom Scoring' : 'Official Team Cup Scoring'}{handicapsOn ? ' · NET' : ''}</div>
          {groupTeams.map(t => (
            <div key={t.id} className="flex justify-between items-center py-1">
              <span className="font-bold">{t.name}</span>
              <span className="text-2xl font-extrabold text-green-800">{fmtPts(matchResult.teamPoints[t.id] || 0)}</span>
            </div>
          ))}
        </div>

        {/* Hole nav */}
        <div className="flex items-center justify-between bg-white rounded-lg p-3">
          <button
            disabled={hole <= 1}
            className="px-3 py-2 rounded bg-gray-100 font-bold disabled:opacity-40"
            onClick={() => setHole(h => Math.max(1, h - 1))}>
            ‹ Prev
          </button>
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-gray-600">Hole</div>
            <div className="text-2xl font-extrabold">{hole}</div>
            <div className="text-xs text-gray-600">Par {par} · SI {si}</div>
          </div>
          <button
            disabled={hole >= 18}
            className="px-3 py-2 rounded bg-gray-100 font-bold disabled:opacity-40"
            onClick={() => setHole(h => Math.min(18, h + 1))}>
            Next ›
          </button>
        </div>

        {/* Per-team score rows */}
        {groupTeams.map(t => (
          <div key={t.id} className="bg-white rounded-lg p-4">
            <div className="text-sm font-extrabold text-green-800 mb-2">{t.name}</div>
            {t.playerIds.map(pid => {
              const player = players.find(p => p.id === pid)
              const gross = Number(myScores?.[hole]?.[pid]) || ''
              const strokes = handicapsOn ? (strokesByPlayer[pid]?.[hole - 1] ?? 0) : 0
              return (
                <div key={pid} className="flex items-center justify-between py-1">
                  <div className="flex-1 mr-2">
                    <div className="font-semibold text-sm">{player?.name || pid}</div>
                    {handicapsOn && strokes > 0 ? (
                      <div className="text-xs text-green-700 font-bold">{'•'.repeat(strokes)} {strokes} stroke{strokes === 1 ? '' : 's'}</div>
                    ) : null}
                  </div>
                  <input
                    type="number" min="1" max="20"
                    inputMode="numeric"
                    className="input w-16 text-center font-extrabold"
                    value={gross}
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      setTeamCupScore(tournamentId, group.id, hole, pid, v)
                    }}
                    placeholder="—" />
                  {handicapsOn && gross > 0 ? (
                    <span className="ml-2 text-xs font-bold text-green-700 w-16 text-right">
                      = {Math.max(0, gross - strokes)}
                    </span>
                  ) : null}
                </div>
              )
            })}
            <div className="text-xs text-gray-600 mt-1">
              Best ball{handicapsOn ? ' (net)' : ''}: {(() => {
                const bests = matchResult.holes[hole - 1]?.bests?.[t.id]
                return bests != null ? bests : '—'
              })()}
            </div>
          </div>
        ))}

        {/* Hole outcome */}
        <div className="bg-white rounded-lg p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-1">Hole {hole} Outcome</div>
          {(() => {
            const cur = matchResult.holes[hole - 1]
            if (!cur || cur.winnerId == null) return <div className="text-sm text-gray-600">Enter every team's scores to lock in the hole.</div>
            if (cur.winnerId === 'tie') return <div className="text-sm font-bold text-amber-700">Halved hole — ½ point each</div>
            const tw = groupTeams.find(t => t.id === cur.winnerId)
            return <div className="text-sm font-bold text-green-800">{tw?.name || 'Team'} wins +1 point</div>
          })()}
        </div>

        {/* Scats banner */}
        {config.scatsEnabled && scats ? (
          <div className="bg-white rounded-lg p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-1">Scats — Hole {hole}</div>
            {(() => {
              const row = scats.perHole.find(r => r.hole === hole)
              if (!row || row.winnerId == null) return <div className="text-sm text-gray-600">Waiting for all groups to score this hole.</div>
              if (row.winnerId === 'tie') return <div className="text-sm font-bold text-amber-700">Tied — no Scats winner</div>
              const wp = players.find(p => p.id === row.winnerId)
              return <div className="text-sm font-bold text-green-800">{wp?.name || row.winnerId} (gross {row.gross})</div>
            })()}
            <div className="text-xs text-gray-500 mt-1">Dollars shown in admin Settlement when round is final.</div>
          </div>
        ) : null}

        {/* CTH (par 3 only) */}
        {config.cthEnabled && isPar3 ? (
          <div className="bg-white rounded-lg p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2">Closest to Hole — Hole {hole}</div>
            <div className="flex flex-wrap gap-1.5">
              {players.map(p => {
                const on = cthSubmittedHere === p.id
                return (
                  <button
                    key={p.id}
                    onClick={() => setTeamCupCth(tournamentId, hole, group.id, p.id)}
                    className={`text-xs px-2 py-1.5 rounded-full border ${on ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-700 border-gray-300'}`}>
                    {on ? '✓ ' : ''}{p.name || p.id}
                  </button>
                )
              })}
              <button
                onClick={() => setTeamCupCth(tournamentId, hole, group.id, 'none')}
                className={`text-xs px-2 py-1.5 rounded-full border ${cthSubmittedHere === 'none' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-gray-300'}`}>
                None
              </button>
            </div>
          </div>
        ) : null}

      </div>
    </Shell>
  )
}

function Shell({ tournament, subtitle, children }) {
  return (
    <div className="min-h-full" style={{ backgroundColor: 'var(--ge-primary)' }}>
      <header className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: 'var(--ge-primary)', color: '#FFFFFF', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <button onClick={() => window.history.back()} className="text-xs font-bold uppercase tracking-widest text-amber-300">← Back</button>
        <div className="text-center flex-1 mx-2">
          <div className="text-xs uppercase tracking-widest opacity-70">Team Cup</div>
          <div className="font-bold text-sm truncate">{tournament?.info?.name || ''}</div>
          {subtitle ? <div className="text-xs opacity-80">{subtitle}</div> : null}
        </div>
      </header>
      <main className="max-w-md mx-auto p-3 pb-12">{children}</main>
    </div>
  )
}

function entriesAsList(obj) {
  return Object.entries(obj || {}).map(([id, v]) => ({ id, ...v }))
}
function fmtPts(n) {
  if (Number.isInteger(n)) return String(n)
  return String(Math.round(n * 10) / 10)
}
